import { useWebsocket } from 'contexts/websocket';
import { useCallback, useEffect, useRef, useState } from 'react';
import chatService from 'services/chat';
import { getLatestChatMessage } from 'utils/chat';
import { ChatMergeMode, ChatRequestLifecycleEvent, CHAT_PAGE_SIZE } from 'utils/chat/chatConstants';
import {
    appendPersistedMessage,
    buildChatReconciliationPayload,
    hasOlderChatMessages,
    mergeFetchedChatPage,
} from 'utils/chat/chatState';
import useRuntimeReconciliationBlock from './useRuntimeReconciliationBlock';

const OlderMessagesLoadingMinDurationMs = 350;
const getReadStateEntry = ({ chat, accountId }) => {
    return (chat?.read_state || []).find((item) => item.account_id === accountId) || null;
};

const shouldMarkLatestMessageAsRead = ({ chat, isOpen, readerAccountId }) => {
    if (!isOpen || !readerAccountId || !chat?.id) return false;

    const latestMessage = getLatestChatMessage(chat);
    if (!latestMessage) return false;

    const readEntry = getReadStateEntry({ chat, accountId: readerAccountId });
    if (typeof chat.messages_version === 'number' && typeof readEntry?.last_read_messages_version === 'number') {
        return readEntry.last_read_messages_version !== chat.messages_version;
    }

    return readEntry?.last_read_message_id !== latestMessage.id;
};

const shouldLoadSuggestions = ({ assignmentId, chat, shouldShowSuggestionsForChat }) => {
    return chat?.assignment_id !== assignmentId || Boolean(shouldShowSuggestionsForChat);
};

/**
 * Full message-thread controller for an opened assignment chat.
 *
 * Use this only when rendering an interactive chat panel. It owns chat creation/resolution,
 * page fetching, older-message pagination, read state, suggestions, and reconciliation.
 * Assistant sends/results are not handled here directly; `useAssistantChatController` emits
 * request-state events that this hook applies to persisted chat state.
 *
 * @param {Object} props
 * @param {string} props.assignmentId Assignment whose thread is being shown.
 * @param {string} props.courseId Course that owns the assignment.
 * @param {string} props.ownerAccountId Account that owns the chat.
 * @param {string} props.readerAccountId Account whose read state should be updated.
 * @param {Function} [props.resolveChat] Optional resolver for an existing chat before creating one.
 * @param {Function} [props.createSuggestionsRequest] Optional request factory for prompt suggestions.
 * @param {Function} [props.initializeChat] Optional post-load reconciliation hook for the chat.
 * @param {boolean} props.isOpen Whether the chat panel is mounted/open.
 * @param {Function} [props.onChatUpdated] Notified whenever local chat state changes.
 * @param {number} [props.refreshIntervalMs=0] Optional polling reconciliation interval.
 * @param {Function} [props.shouldShowSuggestions] Predicate receiving `{ chat }`.
 * @returns {Object} Thread state, suggestion state, pagination actions, and assistant request-state handler.
 */
const useAssignmentChatThread = ({
    assignmentId,
    courseId,
    ownerAccountId,
    readerAccountId,
    resolveChat,
    createSuggestionsRequest,
    initializeChat,
    isOpen,
    onChatUpdated,
    refreshIntervalMs = 0,
    shouldShowSuggestions,
}) => {
    const { isConnected, onMessage, setPingPayload } = useWebsocket();

    const [chat, setChat] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isBootstrapping, setIsBootstrapping] = useState(Boolean(isOpen));
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);

    const chatRef = useRef(null);
    const isReconcilingRef = useRef(false);
    const isLoadingOlderMessagesRef = useRef(false);
    const lastRequestedBeforeMessageIdRef = useRef(null);
    const { isRuntimeReconciliationBlocked, resetRuntimeReconciliationBlock, setRuntimeBlocksReconciliation } =
        useRuntimeReconciliationBlock();

    const showSuggestions = shouldShowSuggestions ? shouldShowSuggestions({ chat }) : false;
    const latestChatMessage = getLatestChatMessage(chat);

    useEffect(() => {
        chatRef.current = chat;
        onChatUpdated?.(chat);
    }, [chat, onChatUpdated]);

    const applyFetchedChat = useCallback((nextChat, { mergeMode = ChatMergeMode.REPLACE } = {}) => {
        if (!nextChat) return null;

        setChat((currentChat) =>
            mergeFetchedChatPage({
                currentChat,
                nextChat,
                mode: mergeMode,
            })
        );

        return nextChat;
    }, []);

    const ensureChat = useCallback(async () => {
        if (!assignmentId || !courseId || !ownerAccountId) return null;

        const currentChat = chatRef.current;
        if (currentChat?.assignment_id === assignmentId) return currentChat;

        return chatService.createChat({
            active: true,
            created_on: Date.now(),
            modified_on: Date.now(),
            owner_account_id: ownerAccountId,
            course_id: courseId,
            assignment_id: assignmentId,
            messages: [],
        });
    }, [assignmentId, courseId, ownerAccountId]);

    const fetchChatPage = useCallback(
        async ({ chatId, beforeMessageId = null, mergeMode = ChatMergeMode.REPLACE }) => {
            if (!chatId) return null;

            const nextChat = await chatService.getChat(chatId, {
                messages_top: CHAT_PAGE_SIZE,
                ...(beforeMessageId ? { before_message_id: beforeMessageId } : {}),
            });

            return applyFetchedChat(nextChat, { mergeMode });
        },
        [applyFetchedChat]
    );

    const reconcileChat = useCallback(
        async (chatId) => {
            if (!chatId || isReconcilingRef.current) return null;
            if (isRuntimeReconciliationBlocked()) return null;

            isReconcilingRef.current = true;

            try {
                return await fetchChatPage({ chatId, mergeMode: ChatMergeMode.REPLACE });
            } finally {
                isReconcilingRef.current = false;
            }
        },
        [fetchChatPage, isRuntimeReconciliationBlocked]
    );

    const loadOlderMessages = useCallback(async () => {
        const currentChat = chatRef.current;
        const beforeMessageId = currentChat?.next_before_message_id || null;

        if (!currentChat?.id || isLoadingOlderMessagesRef.current || !hasOlderChatMessages(currentChat)) return;
        if (!beforeMessageId || lastRequestedBeforeMessageIdRef.current === beforeMessageId) return;

        isLoadingOlderMessagesRef.current = true;
        lastRequestedBeforeMessageIdRef.current = beforeMessageId;
        setIsLoadingOlderMessages(true);
        const loadingStartedAt = Date.now();

        try {
            await fetchChatPage({
                chatId: currentChat.id,
                beforeMessageId,
                mergeMode: ChatMergeMode.PREPEND,
            });
        } finally {
            const elapsedMs = Date.now() - loadingStartedAt;
            const remainingMs = OlderMessagesLoadingMinDurationMs - elapsedMs;

            if (remainingMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, remainingMs));
            }

            isLoadingOlderMessagesRef.current = false;
            setIsLoadingOlderMessages(false);
        }
    }, [fetchChatPage]);

    const onChatRequestStateChange = useCallback(
        (event) => {
            if (!event?.type) return;

            if (event.type === ChatRequestLifecycleEvent.ASSISTANT_MESSAGE_CREATED) {
                const { assistantMessage } = event;

                setChat((currentChat) => {
                    const nextChat = appendPersistedMessage(currentChat, assistantMessage);
                    if (!nextChat) return nextChat;

                    return {
                        ...nextChat,
                        is_processing: false,
                    };
                });

                return;
            }

            if (event.type === ChatRequestLifecycleEvent.REQUEST_FAILED) {
                const shouldPersistFailureState = event.failureKind !== 'disconnected';
                resetRuntimeReconciliationBlock();

                setChat((currentChat) => {
                    if (!currentChat?.is_processing) return currentChat;

                    return {
                        ...currentChat,
                        is_processing: false,
                    };
                });

                const chatId = chatRef.current?.id;
                if (!chatId || !shouldPersistFailureState) return;

                chatService
                    .updateChat(chatId, {
                        is_processing: false,
                        modified_on: Date.now(),
                    })
                    .catch(() => {});

                return;
            }

            if (event.type === ChatRequestLifecycleEvent.RUNTIME_STATE_CHANGED) {
                setRuntimeBlocksReconciliation(event.blocksExternalReconciliation);
                return;
            }

            if (event.type === ChatRequestLifecycleEvent.USER_MESSAGE_CREATED) {
                const { userMessage } = event;

                setChat((currentChat) => {
                    const nextChat = appendPersistedMessage(currentChat, userMessage);
                    if (!nextChat) return nextChat;

                    return {
                        ...nextChat,
                        is_processing: true,
                    };
                });

                const chatId = chatRef.current?.id;
                if (!chatId) return;

                chatService
                    .updateChat(chatId, {
                        is_processing: true,
                        modified_on: userMessage.modified_on || Date.now(),
                    })
                    .catch(() => {});
            }
        },
        [resetRuntimeReconciliationBlock, setRuntimeBlocksReconciliation]
    );

    const updateReadState = useCallback((nextReadState) => {
        setChat((currentChat) => {
            if (!currentChat) return currentChat;

            return {
                ...currentChat,
                read_state: nextReadState || currentChat.read_state || [],
            };
        });
    }, []);

    const markLatestMessageAsRead = useCallback(async () => {
        if (!chat?.id || !latestChatMessage) return;

        const response = await chatService.markRead({
            auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
            chat_id: chat.id,
            last_read_message_id: latestChatMessage.id,
            last_read_messages_version: chat.messages_version,
        });

        return response?.read_state || [];
    }, [chat?.id, chat?.messages_version, latestChatMessage]);

    const hydrateChatThread = useCallback(async () => {
        const ensuredChat = resolveChat ? await resolveChat() : await ensureChat();
        if (!ensuredChat?.id) return ensuredChat;

        const latestChatPage = await fetchChatPage({
            chatId: ensuredChat.id,
            mergeMode: ChatMergeMode.REPLACE,
        });

        return latestChatPage || ensuredChat;
    }, [ensureChat, fetchChatPage, resolveChat]);

    const initializeChatThread = useCallback(
        async (threadChat) => {
            if (!initializeChat) return threadChat;
            return initializeChat(threadChat);
        },
        [initializeChat]
    );

    const loadSuggestions = useCallback(async () => {
        if (!createSuggestionsRequest) {
            setSuggestions([]);
            return;
        }

        try {
            const response = await createSuggestionsRequest();
            setSuggestions(response?.suggestions || []);
        } catch {
            setSuggestions([]);
        }
    }, [createSuggestionsRequest]);

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!isOpen) return {};
            if (isRuntimeReconciliationBlocked()) return {};
            return buildChatReconciliationPayload(chatRef.current);
        });

        return cleanup;
    }, [isOpen, isRuntimeReconciliationBlocked, setPingPayload]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.type !== 'pong' || !Array.isArray(message.chat_reconciliations)) return;

            const currentChatId = chatRef.current?.id;
            if (!currentChatId) return;

            const reconciliation = message.chat_reconciliations.find(({ chat_id }) => chat_id === currentChatId);
            if (!reconciliation?.out_of_sync) return;

            await reconcileChat(currentChatId);
        });

        return cleanup;
    }, [onMessage, reconcileChat]);

    useEffect(() => {
        if (!isConnected || !isOpen || !chat?.id) return;

        reconcileChat(chat.id).catch(() => {
            // best-effort reconciliation
        });
    }, [chat?.id, isConnected, isOpen, reconcileChat]);

    useEffect(() => {
        if (!isOpen || !chat?.id || refreshIntervalMs <= 0) return;

        const intervalId = setInterval(() => {
            reconcileChat(chat.id).catch(() => {
                // best-effort reconciliation
            });
        }, refreshIntervalMs);

        return () => clearInterval(intervalId);
    }, [chat?.id, isOpen, reconcileChat, refreshIntervalMs]);

    useEffect(() => {
        if (!shouldMarkLatestMessageAsRead({ chat, isOpen, readerAccountId })) return;

        let isMounted = true;

        const syncReadState = async () => {
            try {
                const nextReadState = await markLatestMessageAsRead();
                if (!isMounted) return;
                updateReadState(nextReadState);
            } catch {
                // best-effort update
            }
        };

        syncReadState();

        return () => {
            isMounted = false;
        };
    }, [chat, isOpen, markLatestMessageAsRead, readerAccountId, updateReadState]);

    useEffect(() => {
        if (!isOpen) return;

        let cancelled = false;
        const shouldStop = () => cancelled;

        const bootstrap = async () => {
            setIsBootstrapping(true);

            try {
                const hydratedChat = await hydrateChatThread();
                if (shouldStop()) return;

                const initializedChat = await initializeChatThread(hydratedChat);
                if (shouldStop()) return;

                applyFetchedChat(initializedChat, { mergeMode: ChatMergeMode.REPLACE });
                if (shouldStop()) return;

                if (
                    !shouldLoadSuggestions({
                        assignmentId,
                        chat: initializedChat,
                        shouldShowSuggestionsForChat: shouldShowSuggestions
                            ? shouldShowSuggestions({ chat: initializedChat })
                            : false,
                    })
                ) {
                    return;
                }

                if (shouldStop()) return;
                await loadSuggestions();
            } finally {
                if (!shouldStop()) {
                    setIsBootstrapping(false);
                }
            }
        };

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [
        applyFetchedChat,
        assignmentId,
        hydrateChatThread,
        initializeChatThread,
        isOpen,
        loadSuggestions,
        shouldShowSuggestions,
    ]);

    useEffect(() => {
        setChat(null);
        setSuggestions([]);
        setIsBootstrapping(Boolean(isOpen));
        setIsLoadingOlderMessages(false);
        isLoadingOlderMessagesRef.current = false;
        lastRequestedBeforeMessageIdRef.current = null;
        resetRuntimeReconciliationBlock();
    }, [assignmentId, isOpen, resetRuntimeReconciliationBlock]);

    return {
        chat,
        hasOlderMessages: hasOlderChatMessages(chat),
        isBootstrapping,
        isLoadingOlderMessages,
        loadOlderMessages,
        onChatRequestStateChange,
        showSuggestions,
        suggestions,
    };
};

export default useAssignmentChatThread;
